import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { Calendar } from "@msuite/ui/calendar";
import { ContentBox } from "@msuite/ui/content-box";
import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { Email } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { type TabPanelGroups, type TabPanelTypeItem, Tabs } from "@msuite/ui/tabs";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import { getFullName } from "@msuite/utils/users";
import { useState } from "react";
import { useParams } from "react-router";
import { UserAttendances } from "./user-attendances";
import { UserCalendar } from "./user-calendar";
import { UserGeneral } from "./user-general";
import { UserRiskAssessments } from "./user-risk-assessments";
import { UserSettings } from "./user-settings";
import { UserTimeline } from "./user-timeline";

export function User() {
	/** Params */
	const userId = useParams().userId as string;

	/** State */
	const [startDate, setStartDate] = useState(moment().startOf("year").db());
	const [endDate, setEndDate] = useState(moment().endOf("year").db());

	/** Hooks */
	const { t } = useTranslation();
	const { citadel } = useCitadel();
	const { data } = useQuery({
		queryKey: ["users", userId ?? ""],
		queryFn: async () => {
			if (!userId) return null;
			const response = await client.users[":userId"].$get({
				param: {
					userId,
				},
			});
			const data = await responseHandler(response);
			return data.user;
		},
		enabled: !!userId,
	});

	/** Functions */
	function handleOnForward() {
		const newMoment = moment(startDate).add(1, "year");
		setStartDate(newMoment.startOf("year").db());
		setEndDate(newMoment.endOf("year").db());
	}

	function handleOnBack() {
		const newMoment = moment(startDate).subtract(1, "year");
		setStartDate(newMoment.startOf("year").db());
		setEndDate(newMoment.endOf("year").db());
	}

	/** Computed */
	// Reading a colleague's audit trail is a personnel right, and the timeline is
	// the whole of it.
	const canReadUser = citadel?.can("read", "User") === true;
	// Booked times and the risk assessments derived from them are the same body
	// of data, so one right decides whether either tab is reachable. The server
	// binds it to the employee — a member reads their own sheet and nobody
	// else's — so the check carries the row the routes would check.
	const canReadAttendance =
		citadel?.can("read", "Attendance", { orgId: citadel.orgId, userId }) === true;
	// The settings tab is the permissions and roles panel for this user.
	const canManagePermission = citadel?.can("manage", "Permission") === true;

	// The general and calendar tabs stay unconditional: an ordinary member
	// reaches both, and the calendar reads absences, which a member holds.
	const informationPanels: TabPanelTypeItem[] = [
		...(canReadUser
			? [
					{
						cyId: "timeline-tab" as const,
						label: t("timeline.timeline"),
						panel: () => <UserTimeline userId={userId} />,
						errors: [],
					},
				]
			: []),
		{
			cyId: "calendar-tab" as const,
			label: t("dates.calendar"),
			panel: () => (
				<UserCalendar
					userId={userId}
					startDate={startDate}
					endDate={endDate}
				/>
			),
			errors: [],
		},
		...(canReadAttendance
			? [
					{
						cyId: "attendances-tab" as const,
						label: t("users.attendances"),
						panel: () => <UserAttendances userId={userId} />,
						errors: [],
					},
					{
						cyId: "risk-assessments-tab" as const,
						label: t("users.risk_assessments"),
						panel: () => <UserRiskAssessments userId={userId} />,
						errors: [],
					},
				]
			: []),
	];

	const adminPanels: TabPanelTypeItem[] = canManagePermission
		? [
				{
					cyId: "settings-tab" as const,
					label: t("settings.settings"),
					panel: () => <UserSettings userId={userId} />,
					errors: [],
				},
			]
		: [];

	// An empty group would render as a bare tab strip with nothing in it, so the
	// group itself is dropped once its last panel is gated away. The spacer goes
	// with it: it exists to push the admin group to the right edge.
	const groups: TabPanelGroups = [
		{
			name: "general",
			panels: [
				{
					cyId: "general-tab",
					label: t("general.general"),
					panel: () => <UserGeneral userId={userId} />,
					errors: [],
				},
			],
		},
		{ name: "information", panels: informationPanels },
		...(adminPanels.length > 0
			? (["spacer", { name: "admin", panels: adminPanels }] as TabPanelGroups)
			: []),
	];

	/** Render */
	return (
		<ContentBox bottomPadding="none">
			<ContentCard
				title={getFullName(data)}
				icon={icon("User")}
				identifier={data?.id}
			>
				<Email>{guardString(data?.email)}</Email>
			</ContentCard>
			<Tabs
				header={() => {
					return (
						<Calendar.Controls
							onBack={handleOnBack}
							onForward={handleOnForward}
						/>
					);
				}}
				groups={groups}
			/>
		</ContentBox>
	);
}
